import { useEffect, useState } from "react";
import { Box, Button, ButtonGroup, Fade, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import cn from "classnames";
import useAccessRight from "../../hooks/useAccessRight";
import useTranslate from "../../hooks/useTranslate";
import { SES } from "../../constants/accessRights";
import { DAYS_PRODUCTION_CALENDAR_LIST, MONTHS_LIST, WEEK_DAYS_LIST } from "../../constants/robotConfigLists";
import { colorPrimary } from "../../constants/colors";
import { AlertDialog, BlockWithLabel } from "@/ui";
import Slider from "./Slider/Slider";
import { DaysType } from "../../types/ses/sesRobotTypes";
import { ITimeIntervalProps } from "./TimeInterval.props";
import styles from "./TimeInterval.module.scss";
import { Hint } from "@/HOC";

// создание массива данных месяца
const createMonthArray = (): number[][] => {
	const dataArray: number[][] = [[]];
	for (let i = 1; i <= 31; i++) {
		dataArray[dataArray.length - 1].push(i);
		if (i % 7 === 0) dataArray.push([]);
	}
	return dataArray;
};

const TimeInterval = ({
	timeinterval,
	intervalName,
	setChangeFlg,
	changeDaysHandler,
	changeDateHandler,
	changeWeekDaysHandler,
	changeMonthsHandler,
	changeNameHandler,
	addTimePeriodHandler,
	changeTimeIntervalHandler,
	deleteTimeHandler,
	isDisabled,
	tabbedDisplayMode,
}: ITimeIntervalProps): JSX.Element => {
	const [name, setName] = useState<string>(""); // название
	const [days, setDays] = useState<DaysType>("all"); // дни по производственному календарю
	const [weekdays, setWeekdays] = useState<number[]>([]); // дни недели
	const [months, setMonths] = useState<number[]>([]); // месяцы
	const [dates, setDates] = useState<number[]>([]); // дни месяца
	const [showAlertDialogDelInterval, setShowAlertDialogDelInterval] = useState<boolean>(false); // показ диалогового окна при удалении интервала

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	// следим за именем интервала
	useEffect(() => {
		// вписываем поля
		setName(intervalName);
		setDays(timeinterval.days);
		setWeekdays(timeinterval.weekdays);
		setMonths(timeinterval.months);
		setDates(timeinterval.dates);
	}, [timeinterval, intervalName]);

	const handleChangeIntervalName = () => {
		changeNameHandler(name);
	};

	return (
		<Fade in={true} timeout={500}>
			<div className={styles.container}>
				<div
					className={cn(styles.containerBlock, {
						[styles.containerBlockColumn]: tabbedDisplayMode,
					})}
				>
					{/* название */}
					<FormControl fullWidth>
						<Hint hint={translate("hint_intervalName")} position="left">
							<TextField
								fullWidth
								required
								label={translate("input_name")}
								variant="outlined"
								disabled={isDisabled || name === "default"}
								value={name}
								onChange={(e) => setName(e.target.value)}
								onBlur={handleChangeIntervalName}
								error={!name}
								InputProps={{
									style: {
										height: 33,
										fontSize: 13,
										color: colorPrimary,
									},
								}}
								InputLabelProps={{
									style: {
										fontSize: 13,
									},
								}}
								sx={{ '.MuiInputLabel-root[data-shrink="false"]': { top: -8 } }}
							/>
						</Hint>
					</FormControl>

					{/* табы */}
					{tabbedDisplayMode && (
						<ButtonGroup fullWidth sx={{ overflowX: "auto" }}>
							{[...Array.from({ length: tabbedDisplayMode.arrayLength }, (_, idx) => idx), isDisabled ? null : "+"]
								.filter((tab) => tab !== null)
								.map((tabNumber) => (
									<Button
										key={tabNumber}
										variant={tabbedDisplayMode.activeTabIndex === tabNumber ? "contained" : "outlined"}
										sx={{ overflow: "hidden", fontSize: 11 }}
										onClick={() => {
											if (typeof tabNumber === "number") tabbedDisplayMode.setActiveTabIndex(tabNumber);
											else if (tabNumber === "+") tabbedDisplayMode.addIntervalVariantHandler();
										}}
										title={typeof tabNumber === "number" ? `${translate("title_interval")} №${tabNumber + 1}` : translate("button_add")}
									>
										{typeof tabNumber === "number" ? tabNumber + 1 : tabNumber}
									</Button>
								))}
						</ButtonGroup>
					)}

					{/* дни по производственному календарю */}
					<FormControl fullWidth sx={{ '.MuiInputLabel-root[data-shrink="false"]': { top: -8 }, ".MuiSelect-select": { paddingBlock: 0 } }}>
						<InputLabel sx={{ fontSize: 13 }}>{translate("select_days")}</InputLabel>
						<Select
							required
							label={translate("select_days")}
							disabled={isDisabled}
							sx={{
								"& .MuiOutlinedInput-notchedOutline legend": {
									padding: 0,
									fontSize: "10px",
									lineHeight: "1.2",
								},
								"& .MuiOutlinedInput-notchedOutline legend > span": {
									display: "inline-block",
									fontSize: "10px",
									lineHeight: "1.2",
									padding: 0,
									pr: "4px",
								},
							}}
							value={days}
							onChange={changeDaysHandler}
							style={{ fontSize: 13, height: 33, color: colorPrimary }}
						>
							{DAYS_PRODUCTION_CALENDAR_LIST.map(({ day, translation }) => (
								<MenuItem key={day} value={day} sx={{ fontSize: 13, color: colorPrimary }}>
									{translate(translation)}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</div>

				{/* дни недели */}
				<Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
					<BlockWithLabel label={translate("title_daysOfWeek")}>
						<div className={styles.intervalBlock}>
							{WEEK_DAYS_LIST.map(({ day, translationShort }) => (
								<div
									key={day}
									className={cn(styles.weekDay, {
										[styles.weekDayChecked]: weekdays.includes(day),
										[styles.weekDayAccess]: isAccess(SES.ROBOT_EDIT),
										[styles.disabled]: isDisabled,
									})}
									onClick={() => changeWeekDaysHandler(weekdays, day)}
								>
									{translate(translationShort)}
								</div>
							))}
						</div>
					</BlockWithLabel>

					{/* месяцы */}
					<BlockWithLabel label={translate("title_monthes")}>
						<div className={styles.intervalBlock}>
							{MONTHS_LIST.map(({ month, translationShort }) => (
								<div
									key={month}
									className={cn(styles.month, {
										[styles.monthChecked]: months.includes(month),
										[styles.monthAccess]: isAccess(SES.ROBOT_EDIT),
										[styles.disabled]: isDisabled,
									})}
									onClick={() => changeMonthsHandler(months, month)}
								>
									{translate(translationShort)}
								</div>
							))}
						</div>
					</BlockWithLabel>

					{/* дни месяца */}
					<BlockWithLabel label={translate("title_daysOfMonth")}>
						<div className={styles.intervalBlock}>
							<table className={styles.dateTable}>
								<tbody className={styles.tbody}>
									{createMonthArray().map((tr) => (
										<tr key={tr.join(",")} className={styles.tr}>
											{tr.map((day) => (
												<td
													key={day}
													onClick={() => changeDateHandler(dates, day)}
													className={cn({
														[styles.dateTableChecked]: dates.includes(day),
														[styles.dateTableAccess]: isAccess(SES.ROBOT_EDIT),
														[styles.disabled]: isDisabled,
													})}
												>
													{day}
												</td>
											))}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</BlockWithLabel>

					{/* временные интервалы */}
					<BlockWithLabel label={translate("title_periods")} sx={{ gap: "12px" }}>
						{timeinterval.intervals?.map((time, idx, array) => (
							<Slider
								key={time.join("," + idx)}
								intervalName={intervalName}
								time={time}
								idx={idx}
								array={array}
								changeTimeIntervalHandler={changeTimeIntervalHandler}
								deleteTimeHandler={deleteTimeHandler}
								setChangeFlg={setChangeFlg}
								isDisabled={isDisabled}
							/>
						))}
						{/* добавить период */}
						{!isDisabled && (
							<FormControl fullWidth>
								<Button fullWidth variant="outlined" sx={{ fontSize: 11 }} onClick={addTimePeriodHandler} disabled={timeinterval.intervals.length > 3 || isDisabled}>
									{translate("button_addPeriod")}
								</Button>
							</FormControl>
						)}
					</BlockWithLabel>
				</Box>

				{/* удалить интервал */}
				{!isDisabled && tabbedDisplayMode && (
					<FormControl fullWidth>
						<Button color="error" variant="outlined" sx={{ fontSize: 11 }} onClick={() => setShowAlertDialogDelInterval(true)}>
							{translate("button_deleteInterval")}
						</Button>
					</FormControl>
				)}

				{/* удаление интервала */}
				{tabbedDisplayMode && (
					<AlertDialog
						showAlertDialog={showAlertDialogDelInterval}
						setShowAlertDialog={setShowAlertDialogDelInterval}
						submitHandler={tabbedDisplayMode.deleteIntervalVariantHandler}
						title="dialog_deleteInterval"
						description="dialog_deleteIntervalConfirm"
					/>
				)}
			</div>
		</Fade>
	);
};

export default TimeInterval;
